import { useEffect, useState } from 'react'
import { Redirect } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import styled from 'styled-components'
import { Navigation } from 'components'
import colors from 'utils/styling/colors'
import { isSuperAdmin } from '@/hooks/utility/useIsSuperAdmin'
import { userAtom } from '@/atoms/_globalAtoms'
import { requestApiDocsAddress } from '@/api/apiDocs'
import { apiDocsPageEntry } from './apiDocsPageEntry'

const TID = 'api-docs'

/**
 * The admin page that signs a super admin in to the backend's API docs: it
 * trades the admin's session for a one-time docs ticket and replaces itself
 * with the docs, so Back returns to the page before it. The page is internal,
 * so its copy is plain English. Anyone but a super admin, whom the ticket
 * route also requires, is sent to the dashboard.
 */
export const ApiDocsPage = () => {
  const user = useRecoilValue(userAtom)
  const allowed = user !== null && isSuperAdmin(user)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!allowed) return
    let cancelled = false
    requestApiDocsAddress()
      .then((address) => {
        if (!cancelled) window.location.replace(address)
      })
      .catch(() => {
        if (!cancelled)
          setError('The API docs could not be opened. Reload to try again.')
      })
    return () => {
      cancelled = true
    }
  }, [allowed])

  // The signed-in user arrives after the first render on a direct load, so
  // no user yet means still loading, not "not a super admin".
  if (user === null) return null
  if (!allowed) return <Redirect to="/dashboard" />

  return (
    <>
      <Navigation type="admin" />
      <Page data-testid={`${TID}-page`}>
        <Title>{apiDocsPageEntry.title}</Title>
        {error === null ? (
          <p data-testid={`${TID}-opening`}>Opening the API docs…</p>
        ) : (
          <ErrorText role="alert" data-testid={`${TID}-error`}>
            {error}
          </ErrorText>
        )}
      </Page>
    </>
  )
}

const Page = styled.div`
  padding: 32px;
  max-width: 720px;
  color: ${colors('gray1')};
`

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 8px 0;
`

const ErrorText = styled.p`
  color: ${colors('error')};
`
